import React, { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatChartDate } from '../utils/dateUtils';
import BloodTrendChart from './shared/BloodTrendChart';
import { UNITS } from '../utils/unitFormatter';
import { filterByTimeWindow } from './TimeWindowSelector';

/**
 * Compute dashboard domain based on time window
 * Dashboard is different from My Progress:
 * - Time windows end at the LAST DATA ENTRY (not today)
 * - 12M = 12 months back from last entry
 * - 24M = 24 months back from last entry
 * - 36M = 36 months back from last entry
 * - All = first entry to last entry
 */
const computeDashboardDomain = (results, country = 'AU', timeWindow = 'all') => {
  if (!results || results.length === 0) return null;
  
  // Get all result dates sorted
  const resultDates = results
    .map(r => r.date)
    .filter(Boolean)
    .map(date => new Date(date).getTime())
    .filter(ts => !isNaN(ts))
    .sort((a, b) => a - b);
  
  if (resultDates.length === 0) return null;
  
  const firstResultTs = resultDates[0];
  const lastResultTs = resultDates[resultDates.length - 1];
  const lastResultDate = new Date(lastResultTs);
  
  // Calculate domain based on time window - ending at LAST RESULT
  let domainStartTs, domainEndTs;
  
  if (timeWindow === '12m') {
    // 12 months back from last result
    const startDate = new Date(lastResultDate);
    startDate.setMonth(startDate.getMonth() - 12);
    domainStartTs = startDate.getTime();
    domainEndTs = lastResultTs;
  } else if (timeWindow === '24m') {
    // 24 months back from last result
    const startDate = new Date(lastResultDate);
    startDate.setMonth(startDate.getMonth() - 24);
    domainStartTs = startDate.getTime();
    domainEndTs = lastResultTs;
  } else if (timeWindow === '36m') {
    // 36 months back from last result
    const startDate = new Date(lastResultDate);
    startDate.setMonth(startDate.getMonth() - 36);
    domainStartTs = startDate.getTime();
    domainEndTs = lastResultTs;
  } else {
    // 'all' - span from first to last result
    domainStartTs = firstResultTs;
    domainEndTs = lastResultTs;
  }
  
  return {
    domainStartTs,
    domainEndTs,
    margin: { left: 32, right: 0 }
  };
};

const DashboardBloodGraphs = ({ results, clinicalRanges, timeWindow = 'all' }) => {
  const { user } = useAuth();
  const country = user?.country || 'AU';

  // Filter results by time window (presentation-layer only)
  const filteredResults = useMemo(() => {
    return filterByTimeWindow(results, timeWindow);
  }, [results, timeWindow]);

  // Compute domain based on selected time window
  const dashboardDomain = useMemo(() => {
    return computeDashboardDomain(results, country, timeWindow);
  }, [results, country, timeWindow]);

  const getMarkers = () => {
    if (!clinicalRanges) {
      // Default ranges using centralized unit formatter
      return [
        { key: 'wbc', label: 'WBC', color: '#0F766E', unit: UNITS.BLOOD_COUNT, range: { min: 4.0, max: 11.0 } },
        { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: UNITS.BLOOD_COUNT, range: { min: 1.0, max: 4.0 } },
        { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: UNITS.BLOOD_COUNT, range: { min: 150, max: 400 } },
        { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: UNITS.HEMOGLOBIN_SI, range: { min: 130, max: 180 } },
        { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: UNITS.BLOOD_COUNT, range: { min: 2.0, max: 7.5 } }
      ];
    }
    
    return [
      { key: 'wbc', label: 'WBC', color: '#0F766E', unit: clinicalRanges.wbc.unit, range: { min: clinicalRanges.wbc.min, max: clinicalRanges.wbc.max } },
      { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: clinicalRanges.lymphocytes.unit, range: { min: clinicalRanges.lymphocytes.min, max: clinicalRanges.lymphocytes.max } },
      { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: clinicalRanges.platelets.unit, range: { min: clinicalRanges.platelets.min, max: clinicalRanges.platelets.max } },
      { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: clinicalRanges.haemoglobin.unit, range: { min: clinicalRanges.haemoglobin.min, max: clinicalRanges.haemoglobin.max } },
      { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: clinicalRanges.neutrophils.unit, range: { min: clinicalRanges.neutrophils.min, max: clinicalRanges.neutrophils.max } }
    ];
  };

  const markers = getMarkers();

  // Helper to check if a marker has data (in filtered results)
  const hasDataForMarker = (markerKey) => {
    return filteredResults.some(r => r[markerKey] != null);
  };

  // Sort markers: ones with data first, then empty ones (maintaining relative order within each group)
  const sortedMarkers = [...markers].sort((a, b) => {
    const aHasData = hasDataForMarker(a.key);
    const bHasData = hasDataForMarker(b.key);
    if (aHasData && !bHasData) return -1;
    if (!aHasData && bHasData) return 1;
    return 0; // Keep original order within same group
  });

  const renderGraph = (marker) => {
    // Use ALL results for chart data (not just filtered)
    // This ensures line continuity - line connects from data outside the window
    const chartData = results
      .filter(r => r[marker.key] != null)
      .map(r => ({
        date: formatChartDate(r.date, country),
        fullDate: r.date,
        timestamp: new Date(r.date).getTime(),
        value: r[marker.key]
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    // Check if there's data in the filtered window
    const hasData = filteredResults.some(r => r[marker.key] != null);

    return (
      <div key={marker.key} className="bg-white rounded-xl px-0 py-4 md:px-3 md:py-6 border border-slate-200">
        <div className="mb-3 md:mb-4 px-3 md:px-3">
          <h3 className="text-base md:text-lg font-semibold text-slate-900">{marker.label} Trend ({marker.unit})</h3>
          <p className="text-xs md:text-sm text-slate-500">
            Reference range: {marker.range.min}-{marker.range.max}
          </p>
        </div>
        
        {/* Chart Legend */}
        <div className="mb-3 px-3 md:px-3 flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ backgroundColor: marker.color, opacity: 0.15 }}
            ></div>
            <span>Reference range</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-0.5 rounded-full" 
              style={{ backgroundColor: marker.color }}
            ></div>
            <span>Your results</span>
          </div>
        </div>

        {/* Chart - pass dashboardDomain for consistent date formatting */}
        <BloodTrendChart
          data={chartData}
          markerConfig={marker}
          showLegend={false}
          pageDomain={dashboardDomain}
        />

        {!hasData && (
          <div className="mt-3 md:mt-4 text-center px-3 md:px-3">
            <p className="text-xs md:text-sm text-slate-600">No results recorded yet for this marker.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Charts */}
      {sortedMarkers.map(marker => renderGraph(marker))}
    </div>
  );
};

export default DashboardBloodGraphs;
